<template>
  <div class="profile">
    <el-card class="profile-card">
      <template #header>
        <div class="card-header">
          <span>个人管理</span>
        </div>
      </template>
      
      <el-form
        ref="profileFormRef"
        :model="profileForm"
        :rules="profileRules"
        label-width="120px"
        style="max-width: 500px;"
      >
        <el-divider content-position="left">个人信息</el-divider>
        
        <el-form-item label="昵称">
          <el-input 
            v-model="profileForm.nickname" 
            :placeholder="profileForm.nickname ? '' : '暂无昵称（来自微信）'" 
            disabled 
          />
        </el-form-item>
        
        <el-form-item label="用户名" prop="username">
          <el-input 
            v-model="profileForm.username" 
            :placeholder="profileForm.username ? '' : '请输入用户名'" 
          />
        </el-form-item>
        
        <el-form-item label="手机号" prop="mobile">
          <el-input v-model="profileForm.mobile" placeholder="请输入手机号" />
        </el-form-item>
        
        <el-form-item label="邮箱">
          <el-input v-model="profileForm.email" disabled />
        </el-form-item>
        
        <el-form-item label="角色">
          <el-tag :type="profileForm.role === 'admin' ? 'danger' : 'info'">
            {{ profileForm.role === 'admin' ? '管理员' : '普通用户' }}
          </el-tag>
        </el-form-item>
        
        <el-form-item>
          <el-button type="primary" @click="handleUpdateProfile" :loading="profileLoading">
            保存个人信息
          </el-button>
        </el-form-item>
        
        <el-divider content-position="left">修改密码</el-divider>
        
        <el-form-item label="当前密码" prop="currentPassword">
          <el-input
            v-model="profileForm.currentPassword"
            type="password"
            show-password
            placeholder="请输入当前密码"
          />
        </el-form-item>
        
        <el-form-item label="新密码" prop="newPassword">
          <el-input
            v-model="profileForm.newPassword"
            type="password"
            show-password
            placeholder="请输入新密码"
          />
        </el-form-item>
        
        <el-form-item label="确认新密码" prop="confirmPassword">
          <el-input
            v-model="profileForm.confirmPassword"
            type="password"
            show-password
            placeholder="请再次输入新密码"
          />
        </el-form-item>
        
        <el-form-item>
          <el-button type="primary" @click="handleChangePassword" :loading="loading">
            修改密码
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { changePassword, getCurrentUser, updateProfile } from '../api/auth';

export default {
  name: 'Profile',
  setup() {
    const profileForm = reactive({
      nickname: '',
      username: '',
      mobile: '',
      email: '',
      role: '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });

    const profileRules = {
      username: [
        { required: true, message: '请输入用户名', trigger: 'blur' },
        { min: 2, message: '用户名长度不能少于 2 个字符', trigger: 'blur' },
        { max: 50, message: '用户名长度不能超过 50 个字符', trigger: 'blur' }
      ],
      mobile: [
        {
          validator: (rule, value, callback) => {
            if (value && value.trim()) {
              // 手机号格式验证：11位数字，1开头
              const mobileRegex = /^1[3-9]\d{9}$/;
              if (!mobileRegex.test(value.trim())) {
                callback(new Error('请输入正确的手机号格式'));
              } else {
                callback();
              }
            } else {
              callback(); // 手机号可以为空
            }
          },
          trigger: 'blur'
        }
      ],
      currentPassword: [
        { required: true, message: '请输入当前密码', trigger: 'blur' }
      ],
      newPassword: [
        { required: true, message: '请输入新密码', trigger: 'blur' },
        { min: 6, message: '密码长度不能少于 6 个字符', trigger: 'blur' }
      ],
      confirmPassword: [
        { required: true, message: '请确认新密码', trigger: 'blur' },
        {
          validator: (rule, value, callback) => {
            if (value !== profileForm.newPassword) {
              callback(new Error('两次输入的密码不一致'));
            } else {
              callback();
            }
          },
          trigger: 'blur'
        }
      ]
    };

    const loading = ref(false);
    const profileLoading = ref(false);
    const profileFormRef = ref();

    const loadUserInfo = async () => {
      // 优先从后端拉取当前用户，避免本地缓存不一致
      // 使用 skipAutoMessage=true，避免 401 错误时立即清除 token 和跳转
      try {
        console.log('📡 Profile: 开始加载用户信息...');
        const res = await getCurrentUser(true);
        if (res && res.success && res.data) {
          console.log('✅ Profile: 获取用户信息成功:', res.data);
          profileForm.nickname = res.data.nickname || '';
          profileForm.username = res.data.username || '';
          profileForm.mobile = res.data.mobile || '';
          profileForm.email = res.data.email || '';
          profileForm.role = res.data.role || '';
          console.log('✅ Profile: 表单数据已更新:', {
            nickname: profileForm.nickname,
            username: profileForm.username,
            mobile: profileForm.mobile,
            email: profileForm.email,
            role: profileForm.role
          });
          // 同步本地缓存，供其他页面使用
          localStorage.setItem('user', JSON.stringify(res.data));
          return;
        } else {
          console.warn('⚠️ Profile: getCurrentUser 返回失败:', res);
        }
      } catch (e) {
        // 如果获取用户信息失败，使用本地缓存，不要立即清除 token
        console.warn('⚠️ Profile: 获取用户信息失败，使用本地缓存:', e);
      }
      // 回退到本地缓存
      try {
        const userStr = localStorage.getItem('user');
        const user = userStr && userStr !== 'undefined' ? JSON.parse(userStr) : {};
        console.log('📦 Profile: 使用本地缓存:', user);
        profileForm.nickname = user.nickname || '';
        profileForm.username = user.username || '';
        profileForm.mobile = user.mobile || '';
        profileForm.email = user.email || '';
        profileForm.role = user.role || '';
        console.log('✅ Profile: 表单数据已从缓存更新:', {
          nickname: profileForm.nickname,
          username: profileForm.username,
          mobile: profileForm.mobile,
          email: profileForm.email,
          role: profileForm.role
        });
      } catch (e) {
        console.error('❌ Profile: 解析本地缓存失败:', e);
        // 如果解析失败，至少确保字段不为 undefined
        profileForm.nickname = '';
        profileForm.username = '';
        profileForm.mobile = '';
        profileForm.email = '';
        profileForm.role = '';
      }
    };

    const handleUpdateProfile = async () => {
      try {
        // 只验证用户名和手机号字段
        await profileFormRef.value.validateField('username');
        await profileFormRef.value.validateField('mobile');
        
        profileLoading.value = true;

        const response = await updateProfile({
          username: profileForm.username.trim(),
          mobile: profileForm.mobile ? profileForm.mobile.trim() : null
        });

        if (response.success) {
          ElMessage.success('个人信息更新成功');
          // 重新加载用户信息
          await loadUserInfo();
        } else {
          ElMessage.error(response.message || '个人信息更新失败');
        }
      } catch (error) {
        console.error('更新个人信息失败:', error);
        if (error.response && error.response.data && error.response.data.message) {
          ElMessage.error(error.response.data.message);
        } else {
          ElMessage.error('个人信息更新失败');
        }
      } finally {
        profileLoading.value = false;
      }
    };

    const handleChangePassword = async () => {
      try {
        await profileFormRef.value.validate();
        loading.value = true;

        const response = await changePassword({
          currentPassword: profileForm.currentPassword,
          newPassword: profileForm.newPassword
        });

        if (response.success) {
          ElMessage.success('密码修改成功');
          // 清空表单
          profileForm.currentPassword = '';
          profileForm.newPassword = '';
          profileForm.confirmPassword = '';
          profileFormRef.value.resetFields();
        } else {
          ElMessage.error(response.message || '密码修改失败');
        }
      } catch (error) {
        console.error('修改密码失败:', error);
        ElMessage.error('修改密码失败');
      } finally {
        loading.value = false;
      }
    };

    onMounted(() => {
      loadUserInfo();
    });

    return {
      profileForm,
      profileRules,
      loading,
      profileLoading,
      profileFormRef,
      handleUpdateProfile,
      handleChangePassword
    };
  }
};
</script>

<style scoped>
.profile {
  padding: 20px;
}

.profile-card {
  max-width: 600px;
  margin: 0 auto;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.el-divider {
  margin: 24px 0;
}
</style> 